import React from 'react';
import logo from '../assets/logo.png';
import {
  LayoutDashboard, Package, ShoppingCart, Users, Settings, Warehouse, Truck,
  BarChart3, UserCheck, Tag, LogOut, CreditCard, MessageSquare, MapPin,
  Image as ImageIcon, Star, BookOpen,
} from 'lucide-react';

const Sidebar = ({ activeTab, onNavigate, onLogout, userRole }) => {
  const adminSections = [
    {
      label: 'Overview',
      items: [
        { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { id: 'analytics', icon: BarChart3, label: 'Analytics' },
        { id: 'notifications', icon: MessageSquare, label: 'Notifications' },
      ],
    },
    {
      label: 'Catalog',
      items: [
        { id: 'products', icon: Package, label: 'Products' },
        { id: 'offers', icon: Tag, label: 'Offers & Combos' },
        { id: 'inventory', icon: Warehouse, label: 'Inventory' },
      ],
    },
    {
      label: 'Operations',
      items: [
        { id: 'orders', icon: ShoppingCart, label: 'Orders' },
        { id: 'customers', icon: Users, label: 'Customers' },
        { id: 'suppliers', icon: Truck, label: 'Suppliers' },
        { id: 'staff', icon: UserCheck, label: 'Staff Details' },
        { id: 'expenses', icon: CreditCard, label: 'Expenses' },
      ],
    },
    {
      label: 'Content',
      items: [
        { id: 'banners', icon: ImageIcon, label: 'Banner Image' },
        { id: 'promotions', icon: Tag, label: 'Deals & Promotions' },
        { id: 'best-products', icon: Star, label: 'Best Products' },
        { id: 'about-mission', icon: BookOpen, label: 'About — Mission' },
        { id: 'pincode', icon: MapPin, label: 'Pincode' },
        { id: 'reviews', icon: MessageSquare, label: 'Reviews' },
      ],
    },
    {
      label: 'System',
      items: [
        { id: 'settings', icon: Settings, label: 'Settings' },
      ],
    },
  ];

  const staffSections = [
    {
      label: 'Workspace',
      items: [
        { id: 'products', icon: Package, label: 'Products' },
        { id: 'offers', icon: Tag, label: 'Offers & Combos' },
        { id: 'orders', icon: ShoppingCart, label: 'Orders' },
      ],
    },
  ];

  const sections = userRole === 'super_admin' ? adminSections : staffSections;

  return (
    <aside className="fixed inset-y-0 left-0 z-40 flex w-[280px] flex-col bg-pakistan shadow-sidebar">
      {/* Full-height background — always covers entire sidebar */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-moss/25 via-pakistan to-pakistan-dark" />
      <div className="pointer-events-none absolute top-0 right-0 h-full w-px bg-gradient-to-b from-earth/40 via-moss/20 to-transparent" />

      <div className="relative flex min-h-0 flex-1 flex-col">
        {/* Brand — fixed at top */}
        <div className="shrink-0 p-5 pb-3">
          <div className="flex items-center gap-3.5 rounded-2xl border border-earth/50 bg-cornsilk p-3 shadow-[0_4px_24px_rgba(0,0,0,0.22)]">
            <div className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-xl bg-white p-1.5 ring-2 ring-moss/25">
              <div
                className="h-full w-full bg-pakistan"
                style={{
                  WebkitMaskImage: `url(${logo})`,
                  maskImage: `url(${logo})`,
                  WebkitMaskSize: 'contain',
                  maskSize: 'contain',
                  WebkitMaskRepeat: 'no-repeat',
                  maskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                  maskPosition: 'center',
                }}
                role="img"
                aria-label="Manthuli Logo"
              />
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-[17px] font-extrabold leading-tight tracking-tight text-pakistan">
                Manthuli
              </h1>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-tiger">Organic Admin</p>
            </div>
          </div>
        </div>

        {/* Scrollable nav only */}
        <nav className="ui-scrollbar min-h-0 flex-1 space-y-5 overflow-y-auto px-5 pb-3">
          {sections.map((section) => (
            <div key={section.label}>
              <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-earth/50">
                {section.label}
              </p>
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const isActive = activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => onNavigate(item.id)}
                      className={`group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-semibold transition-all duration-200 ${
                        isActive
                          ? 'bg-gradient-to-r from-tiger/25 to-moss/15 text-cornsilk shadow-[inset_0_0_0_1px_rgba(221,161,94,0.35)]'
                          : 'text-cornsilk/55 hover:bg-white/[0.05] hover:text-cornsilk'
                      }`}
                    >
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${
                        isActive
                          ? 'bg-tiger/25 text-earth'
                          : 'bg-white/[0.04] text-cornsilk/45 group-hover:bg-earth/10 group-hover:text-earth'
                      }`}>
                        <item.icon size={16} />
                      </span>
                      <span className="truncate">{item.label}</span>
                      {isActive && (
                        <span className="ml-auto h-1.5 w-1.5 rounded-full bg-earth shadow-[0_0_10px_#DDA15E]" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Logout — fixed at bottom */}
        <div className="shrink-0 border-t border-earth/15 p-5 pt-4">
          <button
            type="button"
            onClick={onLogout}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-semibold text-earth/90 transition-all duration-200 hover:bg-tiger/15 hover:text-cornsilk"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-tiger/15">
              <LogOut size={16} />
            </span>
            <span>Logout</span>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
